import DOMPurify from "dompurify";

/**
 * SS-4071 — sanitizer for the HTML an AI chat answer is injected with.
 *
 * Office Optimizer answers (and the user's own input) go straight into
 * dangerouslySetInnerHTML. Two problems came out of that: markup the app can
 * never render — a Chart.js <canvas>/<script> block showed up as a blank
 * 400x200 box, because scripts inserted via innerHTML do not execute — and
 * markup that DOES run once injected, such as an inline onerror handler, in
 * HTML that is partly derived from patient data.
 *
 * So allow only what the chat legitimately uses: text and formatting, lists,
 * links, and the tables the app turns into bar charts (see MessageList's
 * parseTable). data-* attributes stay allowed by default, which the hidden
 * <span class="intercom-help" data-question="…"> the API embeds relies on.
 *
 * The tag allowlist mirrors the API's po/utils/htmlSanitize.js, which strips
 * the same markup before storing an answer — keep the two in sync. The
 * attribute allowlist is the same set, but DOMPurify applies it globally
 * rather than per tag (the API scopes href to <a> and colspan to cells); the
 * extra leeway is inert markup, and DOMPurify still filters unsafe URLs.
 */
export const ALLOWED_TAGS = [
  "p", "br", "hr", "div", "span",
  "strong", "b", "em", "i", "u", "s", "small", "sub", "sup",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "ul", "ol", "li", "dl", "dt", "dd",
  "blockquote", "code", "pre",
  "a",
  "table", "thead", "tbody", "tfoot", "tr", "th", "td", "caption", "colgroup", "col",
];

export const ALLOWED_ATTR = [
  "class",
  "href", "target", "rel", "title",
  "colspan", "rowspan", "scope",
];

export const sanitizeChatHtml = (html) =>
  typeof html === "string" && html
    ? DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR })
    : "";
